import { createCalendar } from "../calendar/builders.js";
import { createDomainEventDefinition, createRecordDefinition, } from "../domain/builders.js";
import { createRateBuilders } from "../economy/rates.js";
import { createMarket } from "../markets/builders.js";
import { createTask } from "../tasks/builders.js";
import { defineOwnedGame } from "./definition.js";
import { owned } from "./handles.js";
import { createAutomation, createChallenge, createPrestige, createTrigger, createUpgrade, } from "./progression-builders.js";
import { assertOwner, freezeEntries, safePriority, validId } from "./validation.js";
export function createGameKit(options) {
    const owner = Object.freeze({});
    const numbers = options.numbers;
    const kit = {
        numbers,
        rates: createRateBuilders(owner, numbers),
        q: (encoded) => typeof encoded === "number" ? numbers.fromNumber(encoded) : numbers.fromString(encoded),
        scope: (id) => createScope(id, owner),
        resource: (id, value) => createResource(id, value, owner, numbers),
        flow: (id, value) => createFlow(id, value, owner, numbers),
        buyable: (id, value) => createBuyable(id, value, owner, numbers),
        recipe: (id, value) => createRecipe(id, value, owner, numbers),
        allocation: (id, value) => createAllocation(id, value, owner, numbers),
        prestige: (id, value) => createPrestige(id, value, owner),
        upgrade: (id, value) => createUpgrade(id, value, owner, numbers),
        milestone: (id, value) => createTrigger(id, { ...value, kind: "milestone" }, owner),
        achievement: (id, value) => createTrigger(id, { ...value, kind: "achievement" }, owner),
        challenge: (id, value) => createChallenge(id, value, owner),
        automation: (id, value) => createAutomation(id, value, owner),
        task: (id, value) => createTask(id, value, owner, numbers),
        calendar: (id, value) => createCalendar(id, value, owner),
        market: (id, value) => createMarket(id, value, owner, numbers),
        record: (id, value) => createRecordDefinition(id, value, owner),
        eventType: (id, value) => createDomainEventDefinition(id, value, owner),
        scopeActivation: (id, value) => {
            validId(id, "scope activation");
            assertOwner(value.scope, owner, `Scope for ${id}`);
            return owned({ ...value, id }, owner);
        },
        steppedRule: (id, value) => {
            validId(id, "stepped rule");
            assertOwner(value.scope, owner, `Scope for ${id}`);
            return owned({ ...value, id, priority: safePriority(value.priority, id) }, owner);
        },
        defineGame: (input) => defineOwnedGame({ ...input, numbers }, owner),
    };
    return Object.freeze(kit);
}
function createScope(id, owner) {
    validId(id, "scope");
    return owned({ id }, owner);
}
function createResource(id, options, owner, numbers) {
    validId(id, "resource");
    assertOwner(options.scope, owner, `Scope for ${id}`);
    if (!numbers.isFinite(options.initial))
        throw new TypeError(`Initial value for ${id} must be finite`);
    if (options.capacity !== undefined && !numbers.isFinite(options.capacity)) {
        throw new TypeError(`Capacity for ${id} must be finite`);
    }
    if (options.capacity !== undefined &&
        (numbers.cmp(options.capacity, numbers.fromNumber(0)) < 0 ||
            numbers.cmp(options.initial, options.capacity) > 0)) {
        throw new TypeError(`Capacity for ${id} must be nonnegative and at least its initial value`);
    }
    return owned({
        id,
        scope: options.scope,
        initial: options.initial,
        ...(options.capacity === undefined ? {} : { capacity: options.capacity }),
        ...(options.capacityFor === undefined ? {} : { capacityFor: options.capacityFor }),
        overflow: options.overflow ?? "block",
    }, owner);
}
function createFlow(id, options, owner, numbers) {
    validId(id, "flow");
    assertOwner(options.scope, owner, `Scope for ${id}`);
    const priority = safePriority(options.priority, id);
    const consumes = freezeEntries(options.consumes ?? [], owner, numbers, id);
    const produces = freezeEntries(options.produces, owner, numbers, id);
    if (produces.length === 0)
        throw new TypeError(`Flow ${id} must produce at least one resource`);
    return owned({
        id,
        scope: options.scope,
        priority,
        rate: options.rate,
        consumes,
        produces,
        onInputShortage: options.onInputShortage ?? "throttle",
    }, owner);
}
function createBuyable(id, options, owner, numbers) {
    validId(id, "buyable");
    assertOwner(options.scope, owner, `Scope for ${id}`);
    assertOwner(options.currency, owner, `Currency for ${id}`);
    if (options.curve.numericAdapterId !== numbers.id)
        throw new TypeError(`Curve for ${id} uses another numeric adapter`);
    const initialCount = options.initialCount ?? numbers.fromNumber(0);
    const refundRate = options.refundRate ?? numbers.fromNumber(1);
    const zero = numbers.fromNumber(0);
    if (!numbers.isFinite(initialCount) ||
        numbers.cmp(initialCount, zero) < 0 ||
        numbers.cmp(numbers.floor(initialCount), initialCount) !== 0) {
        throw new TypeError(`Initial count for ${id} must be a nonnegative integer quantity`);
    }
    if (!numbers.isFinite(refundRate) ||
        numbers.cmp(refundRate, zero) < 0 ||
        numbers.cmp(refundRate, numbers.fromNumber(1)) > 0) {
        throw new TypeError(`Refund rate for ${id} must be between zero and one`);
    }
    return owned({
        id,
        scope: options.scope,
        currency: options.currency,
        curve: options.curve,
        initialCount,
        refundRate,
    }, owner);
}
function createRecipe(id, options, owner, numbers) {
    validId(id, "recipe");
    assertOwner(options.scope, owner, `Scope for ${id}`);
    const consumes = freezeEntries(options.consumes ?? [], owner, numbers, id);
    const produces = freezeEntries(options.produces, owner, numbers, id);
    if (produces.length === 0)
        throw new TypeError(`Recipe ${id} must produce at least one resource`);
    return owned({ id, scope: options.scope, consumes, produces }, owner);
}
function createAllocation(id, options, owner, numbers) {
    validId(id, "allocation");
    assertOwner(options.scope, owner, `Scope for ${id}`);
    assertOwner(options.budget, owner, `Budget for ${id}`);
    const targets = Object.freeze([...options.targets]);
    if (targets.length === 0 || new Set(targets).size !== targets.length) {
        throw new TypeError(`Allocation ${id} requires unique targets`);
    }
    const initial = {};
    let assigned = numbers.fromNumber(0);
    for (const target of targets) {
        validId(target, "allocation target");
        const amount = options.initial?.[target] ?? numbers.fromNumber(0);
        if (!numbers.isFinite(amount) || numbers.cmp(amount, numbers.fromNumber(0)) < 0) {
            throw new TypeError(`Initial allocation for ${id}:${target} must be nonnegative and finite`);
        }
        initial[target] = amount;
        assigned = numbers.add(assigned, amount);
    }
    if (Object.keys(options.initial ?? {}).some((target) => !targets.includes(target))) {
        throw new TypeError(`Allocation ${id} has an unknown initial target`);
    }
    if (numbers.cmp(assigned, options.budget.initial) > 0) {
        throw new TypeError(`Initial allocation for ${id} exceeds its initial budget`);
    }
    return owned({ id, scope: options.scope, budget: options.budget, targets, initial: Object.freeze(initial) }, owner);
}
//# sourceMappingURL=kit.js.map